<template>
  <article class="faq">
    <translate tag="h1"> Frequently asked questions </translate>
    <client-only>
      <algolia-search :categories="categories" />
    </client-only>
    <faq-category v-for="faq of faqs" :key="faq.categoryName" :faq="faq" />
  </article>
</template>

<script setup lang="ts">
import { useLazyAsyncData, useRoute } from 'nuxt/app'
import SCHEMA_ORGANIZATION from '../../../json/schemaOrganization.json'
import { getFaqMachine } from '../inversify.config'
import { Faq } from '../schemas'

const { t } = useI18n()

const categories = computed<number[]>(() =>
  faqs.value ? faqs.value.map((faq) => faq.categoryId) : [],
)

const lang = useRoute().params.lang || 'en-gb'
const { data: faqs } = useLazyAsyncData(`faqs-${lang}`, getFaqs)

function getFaqs() {
  return new Promise<Faq[]>((resolve) => {
    getFaqMachine((lang as string) || 'en-gb')
      .onTransition((state) => {
        const hasFaqs = state.context.all.length
        if ((state.changed && hasFaqs) || state.matches('error')) {
          if (hasFaqs) {
            resolve(state.context.all)
          }
        }
      })
      .start()
      .send('FETCH_ALL')
  })
}

const headData = computed(() => {
  if (faqs.value === null) {
    return {}
  }
  return {
    title: t('FAQ') + ' | Pitchup.com',
    meta: [
      {
        hid: 'description',
        name: 'description',
        property: 'og:description',
        content:
          'Answers to common questions about using Pitchup.com, including how to cancel a booking, change your payment details or make a group booking.',
      },
    ],
    script: [
      {
        type: 'application/ld+json',
        body: true,
        innerHTML: JSON.stringify(SCHEMA_ORGANIZATION),
        hid: 'schema-organization',
      },
    ],
  }
})

useHead(headData)
</script>

<style scoped>
h1 {
  color: var(--colour-dark-blue);
}

.faq {
  padding: 1em 1em 7em 1em;
  background-color: white;
  font-size: var(--body);
}

.faq :deep(.ui-collapse) {
  border-bottom: none;
  padding: 0 0 0.7em 0;
}

.faq :deep(.ui-collapse-header) {
  border-top: 1px solid #ddd;
  color: var(--colour-text-default);
  font-weight: normal;
  min-height: 0;
  padding: 0.7em 0 0 0;
  text-decoration: none;
}

.faq :deep(.ui-collapse-header:active) {
  background: rgba(67, 89, 125, 0.4);
}

.faq :deep(.ui-collapse.is-active .ui-collapse-header) {
  font-weight: bold;
}

.faq :deep(.ui-collapse-content) {
  margin: 0.7em 0;
  padding: 0.5em 0;
}

.faq :deep(.answer a) {
  box-shadow: inset 0 -2px 0 var(--colour-light-green);
  font-weight: normal;
  text-decoration: none;
  transition:
    box-shadow 0.2s ease-out,
    background-color 0.2s ease-out;
}

.faq :deep(.answer a:active) {
  background-color: rgba(15, 151, 46, 0.7);
  box-shadow: inset 0 -2px 0 rgb(15 151 46 / 0%);
}
</style>
